import { useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Analytics } from '@vercel/analytics/react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './App.css';

// Sections
import Navigation from './sections/Navigation';
import Hero from './sections/Hero';
import Product from './sections/Product';
import Vectoral from './sections/Vectoral';
import Industries from './sections/Industries';
import Contact from './sections/Contact';
import Footer from './sections/Footer';

// Pages
import ClientDetail from './pages/ClientDetail';
import ClientsList from './pages/ClientsList';
import IndustriesPage from './pages/Industries';
import CaseStudy from './pages/CaseStudy';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
// import CoreTeam from './pages/CoreTeam';

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

function App() {
  useEffect(() => {
    // Detect mobile device
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || window.innerWidth < 768;
    
    // Optimize ScrollTrigger for smooth performance
    ScrollTrigger.config({
      autoRefreshEvents: "visibilitychange,DOMContentLoaded,load",
      ignoreMobileResize: true,
    });

    // Configure ScrollTrigger defaults for smooth animations
    // On mobile, we skip ScrollTrigger animations entirely in components
    if (!isMobile) {
      ScrollTrigger.defaults({
        toggleActions: 'play none none none', // Never reverse - prevents flashing
        markers: false,
        refreshPriority: -1,
        once: true, // Only animate once - prevents re-triggering
      });
    }

    // Enable smooth scrolling with GSAP
    gsap.config({
      nullTargetWarn: false,
      // Reduce ticker frequency on mobile for better performance
      autoSleep: isMobile ? 2 : 1,
    });

    // Optimize ScrollTrigger refresh timing with better debouncing
    let refreshTimeout: ReturnType<typeof setTimeout>;
    const refreshScrollTrigger = () => {
      clearTimeout(refreshTimeout);
      refreshTimeout = setTimeout(() => {
        ScrollTrigger.refresh();
      }, isMobile ? 300 : 150);
    };

    // Refresh on load and resize (debounced with better timing)
    let resizeTimer: ReturnType<typeof setTimeout>;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        ScrollTrigger.refresh();
      }, isMobile ? 300 : 200);
    };

    // Use passive listeners for better scroll performance
    const options = { passive: true };
    
    window.addEventListener('resize', handleResize, options);
    window.addEventListener('load', refreshScrollTrigger, options);
    
    // Initial refresh after DOM is ready
    if (document.readyState === 'complete') {
      setTimeout(refreshScrollTrigger, 100);
    } else {
      window.addEventListener('load', refreshScrollTrigger, options);
    }

    // Disable ScrollTrigger on mobile for better performance
    if (isMobile) {
      ScrollTrigger.config({
        autoRefreshEvents: "visibilitychange",
        ignoreMobileResize: true,
      });
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('load', refreshScrollTrigger);
      clearTimeout(resizeTimer);
      clearTimeout(refreshTimeout);
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, []);

  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/"
          element={
            <div className="relative bg-kmg-bg min-h-screen gpu-accelerated">
              <Navigation />
              <main className="smooth-scroll">
                <Hero />
                <Product />
                <Vectoral />
                <Industries />
                {/* <Research /> */}
                {/* <Testimonials /> */}
                {/* <CTA /> */}
                <Contact />
              </main>
              <Footer />
            </div>
          }
        />
        <Route path="/client/:clientName" element={<ClientDetail />} />
        <Route path="/clients" element={<ClientsList />} />
        <Route path="/industries" element={<IndustriesPage />} />
        <Route path="/case-study" element={<CaseStudy />} />
        {/* <Route path="/team" element={<CoreTeam />} /> */}
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
      </Routes>
      <Analytics />
    </BrowserRouter>
  );
}

export default App;
